// The sidebar from design/shell.html: brand, the eleven navigation items in DESIGN.md order, the branch and sync footer.
// Counts appear only when a module reports one; until then the slot is empty rather than a made-up number.
import { NavLink, useLocation } from "react-router";
import { useSessionView } from "../session/store";
import { Icon, Mark, type IconName } from "../ui/icons";

export interface NavItem { key: string; label: string; to: string; icon: IconName; count?: number | undefined; sprint?: number | undefined }

export const NAV: readonly NavItem[] = [
  { key: "today", label: "Today", to: "/today", icon: "today" },
  { key: "appointments", label: "Appointments", to: "/appointments", icon: "calendar" }, // appointments: built in Sprint 2
  { key: "patients", label: "Patients", to: "/patients", icon: "patients" }, // patients: built in Sprint 2
  { key: "orders", label: "Orders", to: "/orders", icon: "orders", sprint: 5 },
  { key: "invoices", label: "Invoices", to: "/invoices", icon: "invoices", sprint: 6 },
  { key: "lab-orders", label: "Lab orders", to: "/lab-orders", icon: "lab", sprint: 7 },
  { key: "purchasing", label: "Purchasing", to: "/purchasing", icon: "purchasing" }, // Sprint 4: Purchasing › Suppliers
  // Sprint 4: Stock opens the stock screens (stock.html); Products sits under it (products.html is data-nav="stock"), reached
  // from Stock's page head and its rows, and Stock stays the active item there, as on Stock take and Transfers.
  { key: "stock", label: "Stock", to: "/stock", icon: "stock" },
  { key: "cash-up", label: "Cash-up", to: "/cash-up", icon: "cash", sprint: 13 },
  { key: "reports", label: "Reports", to: "/reports", icon: "reports", sprint: 14 },
  { key: "settings", label: "Settings", to: "/settings/general", icon: "settings" },
];

/** Sprint 4: the price-list import lives under Stock (`price-import` is `data-nav="stock"`). */
const underStock = (path: string): boolean => path.startsWith("/price-import");

export function Sidebar() {
  const s = useSessionView();
  const location = useLocation();
  const branch = s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? null;
  const org = s?.organisation.tradingName ?? s?.organisation.legalName ?? "Vision X";
  return (
    <aside className="side">
      <div className="brand">
        <Mark />
        <span>Vision X</span>
      </div>
      <nav className="nav" aria-label="Main">
        {NAV.map((item) => (
          <NavLink key={item.key} to={item.to} data-nav={item.key} className={({ isActive }) => (isActive || (item.key === "settings" && location.pathname.startsWith("/settings")) || (item.key === "stock" && (location.pathname.startsWith("/stock") || location.pathname.startsWith("/products"))) || (item.key === "stock" && underStock(location.pathname)) ? "active" : "")}>
            <Icon name={item.icon} />
            {item.label}
            {item.count !== undefined && <span className="cnt">{item.count}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="side-foot">
        <span><b className="strong" style={{ color: "var(--text-2)" }}>{org}{branch ? ` · ${branch}` : ""}</b></span>
        <span>Accounting · not connected</span>
        <span>CRM · not connected</span>
      </div>
    </aside>
  );
}
